import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import * as actions from "../../actionCreator";
import { GlobalApplicationState } from "globalApplicationState";
import { push } from "react-router-redux";
import { Epoch, Event as IEvent, EventManagementFeedFilters, EventListingPage, EventListItem, defaultEventFilter } from "../../models";
import moment from "moment";
import Loading from "modules/common/components/loading";

import DeleteEventConfirmation from "../confirmations/deleteEvent";
import EventFilters from "../event-filters/eventFilters";
import EventPreview from "../event-views/eventPreview";

import TabContent from "pages/common/tabContent";

import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import WarningIcon from '@mui/icons-material/Warning';

import ChipLabels from "modules/common/components/chips/chipLabels";
import Paging from "modules/common/components/paging";
import { ImageScale } from "modules/posts/models";
import Cookies from "js-cookie";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import MoreOptions from "./moreOptions";
import confirm from "utils/notyPopups";
import { SortStyle } from "utils/managementUtils";
import ManagementEmptyResults from "modules/common/components/managementEmptyResults";
import { TenantSettingsTagGroup } from "modules/settings";
import { ROWS_PER_PAGE_COOKIE_NAMES } from "utils/cookie";
import { formatDate } from "utils/dateFormatting";
import { contentBandsSlice } from "modules/contentBands/reducer";
import { isPinnedDeleted } from "modules/common/hooks/data/useContentBands";
import { getHighestRole } from "utils/userRoleUtils";
import { UserRoleStrings } from "modules/authorization/models";
import EventActionToolbar from "./eventActionToolbar";
import EditPublishedContentDialog from "modules/common/components/authoring/dialogs/editPublishedContentDialog";
import { ContentType } from "modules/common/models";
import HoverText from "modules/documents/components/action-buttons/hoverText";

interface HeaderCell {
    id: string;
    label: string;
    ascValue?: SortStyle;
    descValue?: SortStyle;
    clickable?: boolean;
}

class EventList extends React.Component<PropsWithRedux, ComponentState> {
    constructor(props: PropsWithRedux) {
        super(props);        

        this.state = {
            eventPreview: undefined,
            hasFiltersApplied: false,
            isPagedLoad: false,
            selectedItems: [],
            showDeleteEventConfirmation: false,
            openEditPublishedEvent: false,
            showEventPreview: false,
            isContributor: getHighestRole(this.props.user) === UserRoleStrings.CONTRIBUTOR,
        };
    }

    public async componentDidMount() {
        moment.locale("en");
        if (this.props.eventIdToPreview) {
            await this.onPreviewEventId(this.props.eventIdToPreview);
        }
    }

    private onPublishEvent = async (sendNotification: boolean) => {
        this.setState({selectedItems: []})
        this.onHideEventPreview();
        const draftId = this.state.eventPreview?.id;
        if (draftId) {
            await this.props.publishEvent(draftId, sendNotification);
        }
    }

    public render() {
        const { page } = this.props;

        if (!this.props.show) return <React.Fragment/>;

        if (page.isFetching && !this.state.isPagedLoad)
            return <div style={{ paddingTop: "60px" }}><Loading /></div>;

        return (
            <div>
                <EventActionToolbar
                    pageId={page.id}
                    selectedItems={this.state.selectedItems}
                    actions={{
                        onClearAll: this.onClearAllEvents,
                        onPreview: this.onPreviewEvent,
                        onEdit: this.onHandleEdit,
                        onClone: this.onCloneEvent,
                    }}
                    isContributor={this.state.isContributor}
                />
                <TabContent>
                    {this.getFilter()}
                    {this.getList()}
                    {this.state.eventPreview &&
                        <EventPreview
                            show={this.state.showEventPreview}
                            event={this.state.eventPreview}
                            isContributor={this.state.isContributor}
                            onEditDetails={this.onHandleEdit}
                            onClose={this.onHideEventPreview}
                            onPublish={this.onPublishEvent}
                            inEditor={false}
                        />
                    }
                    <DeleteEventConfirmation show={this.state.showDeleteEventConfirmation} multiple={this.state.selectedItems.length > 1} onConfirm={this.onDeleteEvents} onCancel={this.onHideDeleteEventConfirmation} />
                </TabContent>
            </div>
        );
    }

    private formatEpochTime = (time?: Epoch): string => {
        if (time) {
            return moment(new Date(time.date)).format("MMM D, YYYY");
        }
        return "-";
    }

    private formatEpochTimeFromNow = (time?: Epoch): string => {
        if (time) {
            return moment(new Date(time.date)).fromNow();
        }
        return "-";
    }

    private formatTime = (startTime: string, isAllDay?: boolean): string => {
        if (!startTime) return "-";
        if (isAllDay) return formatDate(new Date(startTime), true, false);
        return moment(new Date(startTime)).format("MMM D, YYYY");
    }

    private getFilter = (): JSX.Element => {
        return (
            <EventFilters
                filters={this.props.page.filters}
                pageId={this.props.page.id}
                onChangeFilters={this.onChangeFilters}
                onClearFilters={this.onClearFilters}
                availableTopics={this.props.availableTopics}
                onlyNewer={this.props.page.id === 'scheduled'}
                onlyOlder={this.props.page.id === 'published'}
            />
        );
    }

    private getHeader = (): HeaderCell[] => {
        const { page } = this.props;

        let header: HeaderCell[] = [
            {
                id: "title",
                label: "Event title",
                ascValue: SortStyle.titleAsc,
                descValue: SortStyle.titleDesc,
                clickable: true
            },
        ];

        header.push({ id: "options", label: "" })
        if (page.id === "submissions")
    	    header.push({ id: "submissionTime", label: "Submitted on", ascValue: SortStyle.submittedAsc, descValue: SortStyle.submittedDesc, clickable: true });

        if (page.id === "all")
	      header.push({ id: "eventState", label: "State", ascValue: SortStyle.stateAsc, descValue: SortStyle.stateDesc, clickable: true });

    	header.push({ id: "eventStartTime", label: "Start date", ascValue: SortStyle.startAsc, descValue: SortStyle.startDesc, clickable: true });

        if (page.id !== "drafts" && page.id !== "submissions")
	      header.push({ id: "publishedTime", label: "Publish date", ascValue: SortStyle.publishAsc, descValue: SortStyle.publishDesc, clickable: true });

    	header.push({ id: "modifiedTime", label: "Last modified", ascValue: SortStyle.modifiedAsc, descValue: SortStyle.modifiedDesc, clickable: true });

        if (page.id !== "submissions")
    	    header.push({ id: "eventType", label: "Type", ascValue: SortStyle.typeAsc, descValue: SortStyle.typeDesc, clickable: true });

    	header.push({ id: "author", label: "Published by", ascValue: SortStyle.authorAsc, descValue: SortStyle.authorDesc, clickable: true });

        if (page.id === "published" || page.id === "all")
          header.push({ id: "rsvpCount", label: "RSVPs"});

        header.push({ id: "tags", label: "Topics" });

        return header;
    }

    private getList = (): JSX.Element => {
        const { page } = this.props;
        const { selectedItems } = this.state;

        if (page.isFetching)
            return <div style={{ paddingTop: "60px" }}><Loading /></div>;

        if (!page.events.length)
            return (
        	<ManagementEmptyResults searchType={"Events"} hasFilters={this.state.hasFiltersApplied}/>
            );

        const rowCount = page.events.length || 10;
        return (
            <React.Fragment>
                {this.state.hasFiltersApplied &&
                    <Typography variant="h2" className="filter-results">Results</Typography>
                }
                <EditPublishedContentDialog
                    content={ContentType.Event}
                    open={this.state.openEditPublishedEvent}
                    onEdit={this.onEditEvent}
                    onClose={() => this.setState({openEditPublishedEvent: false})}
                />
	        <TableContainer>
                    <Table size="medium" id="events-table">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        id="all-events-checkbox"
                                        color="primary"
                                        indeterminate={selectedItems.length > 0 && selectedItems.length < rowCount}
                                        checked={rowCount > 0 && selectedItems.length === rowCount}
                                        onChange={this.onSelectAllEvents}
                                        inputProps={{ "aria-label": "select all events" }}
                                    />
                                </TableCell>
                                {this.getHeader().map((headerCell) => (
                                    <TableCell
                                        key={headerCell.id}
                                        align={headerCell.id === "title" ? "left" : "center"}
                                        padding={headerCell.id === "title" ? "none" : "normal"}
                                        onClick={() => this.onClickHeader(headerCell)}
                                    >
                                        <div style={{ paddingBottom: this.props.page.filters.sortType === headerCell.ascValue || this.props.page.filters.sortType === headerCell.descValue ? "8px" : "0px", cursor: headerCell.clickable ? "pointer" : "" }}>
                                            {headerCell.label}
                                            {this.props.page.filters.sortType === headerCell.ascValue && <ArrowUpward htmlColor="#7A7A7A" style={{ position: "relative", top: "8px", paddingLeft: "2px" }} />}
                                            {this.props.page.filters.sortType === headerCell.descValue && <ArrowDownward htmlColor="#7A7A7A" style={{ position: "relative", top: "8px", paddingLeft: "2px" }} />}
                                        </div>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {page.events
                                .map((event, index) => {
                                    const isEventSelected = this.isEventSelected(event);
                                    const labelId = `event-list-checkbox-${index}`;
                                    const colorToUse = event.status.toLowerCase() === "published"
                                        ? "green"
                                        : event.status.toLowerCase() === "draft" 
                                            ? "grey"
                                            : event.status.toLowerCase() === "scheduled" ? "yellow" : "red"

                                    return (
                                        <TableRow
                                            hover
                                            onClick={() => this.handleEventClick(event)}
                                            role="checkbox"
                                            aria-checked={isEventSelected}
                                            tabIndex={-1}
                                            key={event.id}
                                            selected={isEventSelected}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={isEventSelected}
                                                    inputProps={{ "aria-labelledby": labelId }}
                                                    onClick={(ev) => this.onSelectEvent(ev, event)}
                                                />
                                            </TableCell>
                                            <TableCell component="th" id={labelId} scope="row" padding="none">
                                                <span style={{ display: "flex" }}>
                                                    <span style={{ alignSelf: "center"}} id="event-title-span">
                                                        {event.title}
                                                    </span>

                                                    <span style={{ alignSelf: "center", marginLeft: "15px" }}>
                                                        {this.state.isContributor && event.alertUnopened &&
                                                            <HoverText label={<WarningIcon style={{ color: "#e6911a" }} />}>
                                                                Event has been rejected or unpublished
                                                            </HoverText>
                                                        }
                                                    </span>
                                                </span>
                                            </TableCell>
                                            <TableCell >
                                                <MoreOptions
                                                    onPreview={this.onPreviewEvent}
                                                    onClone={this.onCloneEvent}
                                                    onDelete={this.onDeleteEvent}
                                                    onUnpublish={this.onUnpublishEvent}
                                                    onHandleEdit={this.onHandleEdit}
                                                    event={event} />
                                            </TableCell>
                                            {page.id === "submissions" && <TableCell align="center">{this.formatEpochTime(event.lastSubmittedTime)}</TableCell>}
                                            {page.id === "all" &&
                                                <TableCell align="center" style={{ textTransform: "capitalize" }}>
                                                    <span className={`item-state ${colorToUse}`}>{event.status?.toLowerCase() === "submission" ? "needs review" : event.status}</span>
                                                </TableCell>
                                            }
                                            <TableCell align="center">{this.formatTime(event.eventStartTime, event.isAllDayEvent)}</TableCell>
                                            {page.id !== "drafts" && page.id !== "submissions" && <TableCell align="center">{this.formatEpochTime(event.publishedTime)}</TableCell>}
                                            <TableCell align="center">{this.formatEpochTimeFromNow(event.updatedTime)}</TableCell>
                                            {page.id !== "submissions" && <TableCell align="center" style={{ textTransform: "capitalize" }}>{event.eventType}</TableCell>}
                                            <TableCell align="center">{event.authorFullName ? event.authorFullName : event.author}</TableCell>
                                            {(page.id === "published" || page.id === "all") && <TableCell align="center">{event.rsvpCount || "-"}</TableCell>}
                                            <TableCell align="center"><ChipLabels chips={event.tags || []} emptyText="No tags selected" /></TableCell>
                                        </TableRow>
                                    );
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                <Paging
                    currentPage={page.currentPage}
                    items={page.events}
                    totalItems={page.totalEvents}
                    totalPages={page.totalPages}
                    onChangePage={this.onChangePage}
                    resetSelection={this.resetSelection}
                />
            </React.Fragment>
        );
    }

    /**
     * Removes content bands from redux when a pinned post
     * gets deleted. This will force a re-fetch if user goes to content bands
     * page during session and keeps their content up to date.
     */
    private getIsPinnedDeleted = (eventToDelete?: EventListItem): boolean => {
        const { contentBands, publishedContentBands } = this.props.contentBandsState;
        const selectedIds = this.state.selectedItems.map((event: EventListItem) => event.id);
        const idsToCheck = eventToDelete === undefined ? selectedIds : [eventToDelete.id];

        return isPinnedDeleted(contentBands, publishedContentBands, idsToCheck);
    };

    private onDeleteEvent = async (eventToDelete: EventListItem) => {
        const confirmationMessage: string = `You're about to permanently delete '${eventToDelete.title}'. You cannot undo this action.`;
        const isPinnedDeleted = this.getIsPinnedDeleted(eventToDelete);

        if (await confirm.show({
            title: `Delete Event`,
            text: (
                <div>
                    <div>{confirmationMessage}</div>
                    <br />
                    <div>Are you sure?</div>
                </div>
            ),
            yesColor: "#a80000",
            yesText: "Delete",
            noText: "Cancel"
        })) {
            if (isPinnedDeleted)
                this.props.clearContentBands();

            this.props.deleteEvents([eventToDelete]).then(() => {
                this.onClearAllEvents();
            });
        }
    }

    private onUnpublishEvent = async (eventToUnpublish: EventListItem) => {
        const confirmationMessage: string = `Unpublishing an event will reset its RSVP report, clearing the list of users who have submitted their RSVP and their response to any event questions.`;

        if (await confirm.show({
            title: `Unpublish Event`,
            text: (
                <div>
                    <div>{confirmationMessage}</div>
                    <br />
                    <div>Would you like to proceed?</div>
                </div>
            ),
            yesColor: "#3b78ab",
            yesText: "Unpublish",
            noText: "Cancel"
        })) {
            this.props.unpublishEvent(eventToUnpublish.id).then(() => {
                this.onClearAllEvents();
            });
        }
    }

    private isEventSelected = (event: EventListItem): boolean => {
        return this.state.selectedItems.findIndex((selectedItem) => selectedItem.id === event.id) !== -1;
    }

    private onChangeFilters = (filters: Partial<EventManagementFeedFilters>) => {
        this.setState({ hasFiltersApplied: true, isPagedLoad: true });
        const updatedFilters = {...this.props.page.filters, ...filters}
        this.props.fetchPage(1, updatedFilters);
    }

    private onClearFilters = () => {
        this.setState({ hasFiltersApplied: false, isPagedLoad: true });
        this.props.fetchPage(1, defaultEventFilter(this.props.page.id));
    }

    private onClearAllEvents = () => this.setState({ selectedItems: [] });

    private onCloneEvent = (eventToClone?: EventListItem) => {
        let toPreview = (eventToClone === undefined) ? this.state.selectedItems[0] : eventToClone

        this.props.cloneEvent(toPreview.draftId).then((response) => {
            if (!!response) {
                this.props.redirectTo("/" + this.props.tenant + "/admin/events/edit/" + response.id);
                this.onClearAllEvents();
            }
        });
    }

    private handleEventClick = (event: EventListItem) => {
        if (event.publishedTime !== null) {
            this.onPreviewEvent(event);
        }
        else {
            this.props.redirectTo("/" + this.props.tenant + "/admin/events/edit/" + event.id);
        }
    }

    private onDeleteEvents = () => {
        this.onHideDeleteEventConfirmation();
        this.props.deleteEvents(this.state.selectedItems);
        this.onClearAllEvents();
    }

    private onHandleEdit = (eventSelected?: EventListItem) => {
        const toEdit = eventSelected ?? (this.state.selectedItems[0] ?? this.state.eventPreview);

        // special case: a contributor tries to edit an already published event
        this.state.isContributor && toEdit.publishedTime !== null && toEdit.status.toLowerCase() === "published"
            ? this.setState({openEditPublishedEvent: true, showEventPreview: false, selectedItems: [toEdit]})
            : this.onEditEvent(eventSelected);
    }

    private onEditEvent = (eventSelected?: EventListItem) => {
        let toViewId = eventSelected?.draftId ??
            (this.state.selectedItems[0]?.draftId ?? this.state.eventPreview?.id);
        this.props.redirectTo(`/${this.props.tenant}/admin/events/edit/${toViewId}`);
    }

    private onHideDeleteEventConfirmation = () => {
        this.setState({ showDeleteEventConfirmation: false });
    }

    private onHideEventPreview = () => {
        this.setState({ showEventPreview: false });
        if (this.props.eventIdToPreview) {
            this.props.redirectTo("/" + this.props.tenant + "/admin/events");
        }
    }

    private onPreviewEvent = async (eventToDisplay?: EventListItem) => {
        const toPreview = (eventToDisplay === undefined) ? this.state.selectedItems[0] : eventToDisplay

        await this.onPreviewEventId(toPreview.draftId, toPreview.status);
    }

    private onPreviewEventId = async (eventId: string, eventStatus?: string) => {
        try {
            const draft = await this.props.getDraft(eventId, ImageScale.Modal);
            this.setState({ eventPreview: {...draft, status: eventStatus}, showEventPreview: true });
        } catch (error) {
            this.setState({ eventPreview: undefined, showEventPreview: false });
        }
    }

    private onSelectAllEvents = () => {
        if (this.state.selectedItems.length === this.props.page.events.length)
            this.setState({ selectedItems: [] });
        else
            this.setState({ selectedItems: this.props.page.events });
    }

    private onSelectEvent = (ev, event: EventListItem) => {
        let selectedItems = this.state.selectedItems.slice();
        ev.stopPropagation();

        if (selectedItems.findIndex((selectedItem) => selectedItem.id === event.id) === -1)
            selectedItems = selectedItems.concat([event]);
        else
            selectedItems = selectedItems.filter(selectedItem => selectedItem.id !== event.id);

        this.setState({ selectedItems });
    }

    //Default to descending, if currently descending make it ascending.
    private onClickHeader = (header: HeaderCell) => {
        const rowsPerPage = Cookies.get(ROWS_PER_PAGE_COOKIE_NAMES.DEFAULT);

        if (header.descValue === undefined || header.ascValue === undefined) return;

        const newSortType = this.props.page.filters.sortType === header.descValue
            ? header.ascValue
            : header.descValue;

        this.props.fetchPage(
            1,
            {...this.props.page.filters, sortType: newSortType},
            Number(rowsPerPage) ?? 10
        );
    }

    private resetSelection = () => {
        this.setState({selectedItems: []});
    }

    private onChangePage = (page: number, rowsPerPage: number ) => {
        this.setState({ isPagedLoad: true });
        this.props.fetchPage(
            page + 1,
            {...this.props.page.filters},
            rowsPerPage
        );
    }
}


interface ComponentProps {
  show: boolean;
  page: EventListingPage;
  fetchPage: (pageNumber: number, filters: Partial<EventManagementFeedFilters>, pageAmount?: number) => void;
  availableTopics: TenantSettingsTagGroup[];
  eventIdToPreview?: string
}

interface ComponentState {
    eventPreview: IEvent | undefined;
    hasFiltersApplied: boolean;
    isPagedLoad: boolean;
    selectedItems: EventListItem[];
    showDeleteEventConfirmation: boolean;
    openEditPublishedEvent: boolean;
    showEventPreview: boolean;
    isContributor: boolean;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        tenant: state.tenant.id,
        contentBandsState: state.contentBands,
        user: state.settings.currentUser
    }),
    {
        cloneEvent: actions.cloneEvent,
        deleteEvents: actions.deleteEvents,
        publishEvent: actions.publishEvent,
        unpublishEvent: actions.unpublishEvent,
        getDraft: actions.getDraft,
        redirectTo: push,
        clearContentBands: contentBandsSlice.actions.CLEAR_BANDS
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(EventList);
